import { useMemo } from 'react';
import { FormField } from '@src/components/SmartDataTable/types';
import { getPhoneFieldConfig, cleanPhoneNumber } from '@src/utils/phoneUtils';

export const useInsideStockPurchasesFormFields = (): ((
  record?: any
) => FormField[]) => {
  // Memoized optionRender functions to prevent infinite re-renders
  const supplierOptionRender = useMemo(
    () => (item: any) => (item ? `${item.name} (${item.phone})` : ''),
    []
  );

  const supplierOptionLabel = useMemo(
    () => (item: any) => (item ? `${item.name} (${item.phone})` : ''),
    []
  );

  return useMemo(
    () => (record?: any) =>
      [
        // Row 1: Supplier ID, Invoice ID
        {
          name: 'supplier_id',
          label: 'Supplier',
          type: 'resourceSelect',
          required: false,
          placeholder: 'Search and select supplier...',
          searchSelectProps: {
            resource: 'inside-inventory-suppliers',
            optionValue: 'id',
            optionLabel: supplierOptionLabel,
            searchFields: ['name', 'phone'],
            optionRender: supplierOptionRender,
            allowClear: true,
          },
          layout: { span: 12 },
          defaultValue: record?.supplier?.id || record?.supplier_id,
          props: {
            validateTrigger: ['onBlur', 'onChange'],
            validateOnMount: false,
            validateFirst: false,
          },
          onChange: (value: any, form: any) => {
            if (value?.data) {
              const supplier = value.data;

              const supplierName = (supplier.name || '').toString().trim();

              // Clean and extract mobile number without country code
              const supplierMobile = cleanPhoneNumber(supplier.phone || '');

              if (form && typeof form.setFieldsValue === 'function') {
                form.setFieldsValue({
                  supplier_name: supplierName,
                  supplier_mobile: supplierMobile,
                });
              }
            } else {
              if (form && typeof form.setFieldsValue === 'function') {
                form.setFieldsValue({
                  supplier_name: '',
                  supplier_mobile: '',
                });
              }
            }
          },
        },
        {
          name: 'invoice_id',
          label: 'Invoice ID',
          type: 'text',
          required: false,
          placeholder: 'Enter invoice ID...',
          defaultValue: record?.invoice_id,
          layout: { span: 12 },
        },

        // Row 2: Supplier Name, Supplier Mobile
        {
          name: 'supplier_name',
          label: 'Supplier Name',
          type: 'text',
          required: true,
          placeholder: 'Auto-filled from supplier selection...',
          defaultValue: record?.supplier?.name || record?.supplier_name,
          layout: { span: 12 },
          props: {
            size: 'middle',
          },
          tooltip:
            'This field is automatically filled when you select a supplier',
        },
        {
          name: 'supplier_mobile',
          label: 'Supplier Mobile',
          ...getPhoneFieldConfig(false, 'Auto-filled from supplier selection'),
          defaultValue: record?.supplier?.mobile_no || record?.supplier_mobile,
          props: {
            ...getPhoneFieldConfig().props,
            size: 'middle',
            className: 'compact-phone-field',
          },
          layout: { span: 12 },
          tooltip:
            'This field is automatically filled when you select a supplier',
        },

        // Row 3: Bill Amount
        {
          name: 'bill_amount',
          label: 'Bill Amount',
          type: 'number',
          required: true,
          placeholder: '0.00',
          defaultValue: record?.bill_amount,
          min: 0,
          step: 0.01,
          layout: { span: 12 },
        },
      ] as FormField[],
    []
  );
};
